"use client";

import * as React from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { Search, Users, Megaphone, MessageCircle } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useChatShell } from "@/components/chat/chat-shell";
import { COMMUNITY_LINKS, CommunityLinkIcon, trackCommunityLink } from "./community-links";
import { openCommandPalette } from "./CommandPaletteMount";
import { openWhatsNew } from "./WhatsNew";
import { railButtonClass, RailLabel } from "./rail-item";

type Variant = "desktop" | "mobile";

const CHAT_LABEL = {
  mention: "Chat, you were mentioned",
  message: "Chat, new messages",
  new: "Chat, new",
  none: "Chat",
} as const;

export function RailUtilities({
  collapsed = false,
  variant = "desktop",
  hasUnreadUpdates = false,
  onNavigate,
}: {
  collapsed?: boolean;
  variant?: Variant;
  hasUnreadUpdates?: boolean;
  onNavigate?: () => void;
}) {
  const isMobile = variant === "mobile";
  const cls = railButtonClass(collapsed, isMobile);
  const chat = useChatShell();
  const onCommunityPage = usePathname() === "/community";
  const iconSize = isMobile ? "size-[18px] text-zinc-500" : "size-4";
  const chatBadge = chat && (chat.unread ?? (chat.introSeen ? null : "new"));
  const newTag = "rounded bg-primary/15 px-1 text-[10px] font-semibold uppercase leading-[14px] tracking-[0.06em] text-primary";

  return (
    <div
      className={cn(
        "flex flex-col gap-0.5",
        !isMobile && (collapsed ? "px-1.5 pb-1 pt-2" : "px-2 pb-1 pt-2"),
      )}
    >
      {!isMobile && (
        <button
          type="button"
          title={collapsed ? "Search" : undefined}
          aria-label="Search tools and pages with Command K or Control K"
          aria-haspopup="dialog"
          onClick={() => {
            onNavigate?.();
            openCommandPalette();
          }}
          className={cls}
        >
          <Search className={cn(iconSize, "shrink-0")} aria-hidden="true" />
          <RailLabel label="Search" collapsed={collapsed} isMobile={isMobile} />
        </button>
      )}

      {chat ? (
        <Link
          href="/community"
          title={!isMobile && collapsed ? "Community" : undefined}
          aria-current={onCommunityPage ? "page" : undefined}
          onClick={() => onNavigate?.()}
          className={cn(
            railButtonClass(collapsed, isMobile, { quiet: !onCommunityPage }),
            onCommunityPage && (isMobile ? "bg-primary/10 text-primary" : "bg-white/[0.08] text-white"),
          )}
        >
          {onCommunityPage && !isMobile && <span className="absolute left-0 top-1/2 h-4 w-[3px] -translate-y-1/2 rounded-r-full bg-primary" />}
          <Users className={cn(iconSize, "shrink-0", onCommunityPage && "text-primary")} aria-hidden="true" />
          <RailLabel label="Community" collapsed={collapsed} isMobile={isMobile} />
        </Link>
      ) : (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              title={!isMobile && collapsed ? "Community" : undefined}
              aria-label="Community — Discord, YouTube, X"
              className={cls}
            >
              <Users className={cn(iconSize, "shrink-0")} aria-hidden="true" />
              <RailLabel label="Community" collapsed={collapsed} isMobile={isMobile} />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" side={isMobile ? "top" : "right"} sideOffset={10} className="w-48">
            {COMMUNITY_LINKS.map(({ platform, label, href }) => (
              <DropdownMenuItem
                key={platform}
                onSelect={() => {
                  trackCommunityLink(platform);
                  onNavigate?.();
                  window.open(href, "_blank", "noopener,noreferrer");
                }}
              >
                <CommunityLinkIcon platform={platform} className="mr-2 size-4" />
                {label}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      )}

      {chat && (
        <button
          type="button"
          title={!isMobile && collapsed ? "Chat" : undefined}
          aria-label={CHAT_LABEL[chatBadge ?? "none"]}
          aria-pressed={isMobile ? undefined : chat.open && !chat.pageSlot}
          onClick={() => {
            onNavigate?.();
            if (chat.pageSlot) chat.focusPage();
            else chat.setOpen(isMobile || !chat.open);
          }}
          className={cn(cls, !isMobile && chat.open && !chat.pageSlot && "bg-white/[0.08] text-white")}
        >
          <span className="relative flex shrink-0">
            <MessageCircle className={iconSize} aria-hidden="true" />
            {chatBadge === "mention" ? (
              <span className="absolute -right-1.5 -top-1.5 flex size-3.5 items-center justify-center rounded-full bg-primary text-[10px] font-semibold leading-none text-black" aria-hidden="true">
                @
              </span>
            ) : chatBadge === "message" ? (
              <span className="absolute -right-0.5 -top-0.5 size-2 rounded-full bg-primary" aria-hidden="true" />
            ) : chatBadge === "new" && collapsed && !isMobile ? (
              <span className={cn(newTag, "absolute -top-2 left-3")} aria-hidden="true">
                New
              </span>
            ) : null}
          </span>
          <RailLabel label="Chat" collapsed={collapsed} isMobile={isMobile} />
          {chatBadge === "new" && !(collapsed && !isMobile) && (
            <span className={cn(newTag, "ml-auto")} aria-hidden="true">
              New
            </span>
          )}
        </button>
      )}

      <button
        type="button"
        title={!isMobile && collapsed ? "What's new" : undefined}
        aria-label={hasUnreadUpdates ? "What's new, unread updates" : "What's new"}
        onClick={() => {
          onNavigate?.();
          openWhatsNew();
        }}
        className={cls}
      >
        <span className="relative flex shrink-0">
          <Megaphone className={iconSize} aria-hidden="true" />
          {hasUnreadUpdates ? (
            <span
              className="absolute -right-0.5 -top-0.5 size-2 rounded-full bg-primary"
              aria-hidden="true"
            />
          ) : null}
        </span>
        <RailLabel label="What's new" collapsed={collapsed} isMobile={isMobile} />
      </button>
    </div>
  );
}
